<template>
  <div class="tab-content auditor-seat-management">
    <div class="main-header">
      <div>
        <h1>{{ $t('controlCenter.auditorSeatManagement.title') }}</h1>
      </div>
    </div>

    <div v-loading="loading" class="seat-sections">
      <!-- 席位网格 -->
      <div class="app-section">
        <div v-if="loadErrorMessage" class="empty-section error-section">
          <p class="error-message"><i class="fas fa-circle-exclamation"></i>{{ loadErrorMessage }}</p>
          <p class="activate-link-wrapper">
            <a class="activate-link" href="javascript:void(0)" @click="goToActivate">{{ $t('controlCenter.auditorSeatManagement.goToActivate') }}</a>
            <span class="activate-link-separator">|</span>
            <a class="activate-link" href="javascript:void(0)" @click="goToBuy">{{ $t('controlCenter.auditorSeatManagement.buyNow') }}</a>
          </p>
        </div>
        <div v-else-if="seats.length === 0" class="empty-section">
          <p>{{ $t('controlCenter.auditorSeatManagement.empty') }}</p>
          <p class="activate-link-wrapper">
            <a class="activate-link" href="javascript:void(0)" @click="goToBuy">{{ $t('controlCenter.auditorSeatManagement.buyNow') }}</a>
          </p>
        </div>
        <div v-else class="app-grid">
          <div
            v-for="seat in seats"
            :key="seat.id"
            class="seat-card"
            :class="{ 'seat-unassigned': !seat.assignedAuditor }"
          >
            <div class="seat-icon-wrapper" :class="{ 'icon-grayscale': !seat.assignedAuditor }">
              <i class="fas fa-user-tie"></i>
            </div>
            <div class="seat-card-body">
              <div
                v-if="editingSeatId === seat.id"
                class="seat-name-editor"
                @click.stop
              >
                <input
                  ref="seatNameInput"
                  v-model.trim="editingName"
                  class="seat-name-input"
                  type="text"
                  maxlength="50"
                  @blur="commitRename(seat)"
                  @keydown.enter.prevent="$event.target.blur()"
                  @keydown.esc.prevent="cancelRename(seat)"
                />
              </div>
              <div
                v-else
                class="app-name"
                :title="seat.name"
                @click="startRename(seat)"
              >{{ seat.name }}</div>
              <div class="seat-auditor">
                <span v-if="seat.assignedAuditor" class="auditor-assigned">
                  <i class="fas fa-circle-check"></i>
                  {{ seat.assignedAuditor.name || seat.assignedAuditor.email || seat.assignedAuditor.id }}
                </span>
                <span v-else class="auditor-unassigned">
                  <i class="fas fa-circle-minus"></i>
                  {{ $t('controlCenter.auditorSeatManagement.unassigned') }}
                </span>
              </div>
            </div>
            <div class="app-actions">
              <button
                v-if="!seat.assignedAuditor"
                class="action-btn assign"
                @click="goToAssignPage(seat)"
                :disabled="actionLoading === seat.id"
              >
                <i class="fas fa-user-plus"></i>
                <span>{{ $t('controlCenter.auditorSeatManagement.actions.assign') }}</span>
              </button>
              <button
                v-else
                class="action-btn unassign"
                @click="handleUnassign(seat)"
                :disabled="actionLoading === seat.id"
              >
                <i class="fas fa-user-minus"></i>
                <span>{{ $t('controlCenter.auditorSeatManagement.actions.unassign') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { iamApi } from '@/api/iam'
import { getUserInfo } from '@/utils/auth'

export default {
  name: 'AuditorSeatManagement',
  data() {
    return {
      loading: false,
      actionLoading: null,
      subscription: null,
      seats: [],
      loadErrorMessage: '',
      editingSeatId: null,
      editingName: ''
    }
  },
  computed: {
    accountId() {
      const userInfo = getUserInfo()
      return userInfo?.accountId || userInfo?.sub || userInfo?.userId || 'default'
    }
  },
  created() {
    this.fetchSubscription()
  },
  methods: {
    async fetchSubscription() {
      this.loading = true
      this.loadErrorMessage = ''
      try {
        const response = await iamApi.getSubscription(this.accountId)
        this.subscription = response || null
        this.seats = this.subscription?.auditorSeats || []
      } catch (error) {
        console.error('Failed to fetch subscription:', error)
        const errorMsg = error?.response?.data?.message || error?.response?.data?.detail || this.$t('controlCenter.auditorSeatManagement.loadError')
        this.loadErrorMessage = errorMsg
      } finally {
        this.loading = false
      }
    },
    goToActivate() {
      this.$router.push({
        path: '/controlCenter',
        query: { tab: 'appManagement' }
      }).catch(() => {})
    },
    goToBuy() {
      this.$router.push('/buy').catch(() => {})
    },
    goToAssignPage(seat) {
      this.$router.push({
        path: '/controlCenter',
        query: { tab: 'auditorSeatAssign', seatId: seat.id }
      }).catch(() => {})
    },
    startRename(seat) {
      if (this.actionLoading === seat.id) return
      this.editingSeatId = seat.id
      this.editingName = seat.name
      this.$nextTick(() => {
        // refs 是数组（v-for 中同名 ref），找到对应 input
        const inputs = this.$refs.seatNameInput
        const target = Array.isArray(inputs) ? inputs.find(el => el) : inputs
        if (target && target.focus) {
          target.focus()
          try { target.select() } catch (_) {}
        }
      })
    },
    cancelRename(seat) {
      if (this.editingSeatId !== seat.id) return
      this.editingSeatId = null
      this.editingName = ''
      // 还原焦点到席位名字上，避免 blur 再次触发 commitRename（通过修改 editingSeatId 已不会触发）
    },
    async commitRename(seat) {
      if (this.editingSeatId !== seat.id) return
      const newName = (this.editingName || '').trim()
      const originalName = seat.name
      // 未改名或空名：直接退出编辑（空名还原）
      if (!newName) {
        this.editingSeatId = null
        this.editingName = ''
        return
      }
      if (newName === originalName) {
        this.editingSeatId = null
        this.editingName = ''
        return
      }
      this.actionLoading = seat.id
      try {
        await iamApi.renameSeat(this.accountId, seat.id, newName)
        seat.name = newName
        this.$message.success(this.$t('controlCenter.auditorSeatManagement.messages.renameSuccess'))
        this.editingSeatId = null
        this.editingName = ''
      } catch (error) {
        console.error('Failed to rename seat:', error)
        const errorMsg = error?.response?.data?.message || error?.response?.data?.detail || this.$t('controlCenter.auditorSeatManagement.messages.renameError')
        this.$message.error(errorMsg)
        // 失败：保留编辑态并继续聚焦，方便用户继续改
        this.$nextTick(() => {
          const inputs = this.$refs.seatNameInput
          const target = Array.isArray(inputs) ? inputs.find(el => el) : inputs
          if (target && target.focus) target.focus()
        })
      } finally {
        if (this.actionLoading === seat.id) this.actionLoading = null
      }
    },
    async handleUnassign(seat) {
      this.actionLoading = seat.id
      try {
        const response = await iamApi.unassignAuditor(this.accountId, seat.id)
        const successMsg = (response && (response.message || response.detail)) || this.$t('controlCenter.auditorSeatManagement.messages.unassignSuccess')
        this.$message.success(successMsg)
        this.fetchSubscription()
      } catch (error) {
        console.error('Failed to unassign auditor:', error)
        const errorMsg = error?.response?.data?.message || error?.response?.data?.detail || this.$t('controlCenter.auditorSeatManagement.messages.unassignError')
        this.$message.error(errorMsg)
      } finally {
        this.actionLoading = null
      }
    }
  }
}
</script>

<style scoped>
.auditor-seat-management {
  padding: 24px;
}

.main-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.main-header h1 {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: #1f2937;
}

.btn {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  transition: all 0.2s;
}

.btn:hover:not(:disabled) {
  border-color: #9ca3af;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
}

.btn-primary:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn-danger {
  background: #dc2626;
  color: #fff;
  border-color: #dc2626;
}

.btn-danger:hover:not(:disabled) {
  background: #b91c1c;
}

.seat-sections {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.app-section {
  display: flex;
  flex-direction: column;
}

.empty-section {
  padding: 24px 0;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
}

.empty-section.error-section {
  color: #b91c1c;
}

.empty-section.error-section .error-message {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 500;
  color: #b91c1c;
}

.empty-section.error-section .error-message i {
  font-size: 16px;
}

.empty-section .activate-link-wrapper {
  margin: 8px 0 0 0;
}

.empty-section .activate-link {
  display: inline-block;
  color: #2563eb;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
}

.empty-section .activate-link:hover {
  color: #1d4ed8;
}

.empty-section .activate-link-separator {
  display: inline-block;
  margin: 0 8px;
  color: #d1d5db;
  font-size: 14px;
}

/* ==== 紧凑网格布局 ==== */
.app-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
}

.seat-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
  transition: all 0.15s ease;
}

.seat-card:hover {
  border-color: #c7d2fe;
  box-shadow: 0 3px 10px rgba(102, 126, 234, 0.1);
}

.seat-card.seat-unassigned {
  background: #fafafa;
}

.seat-icon-wrapper {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.25);
  flex-shrink: 0;
  font-size: 13px;
  color: #fff;
}

.seat-icon-wrapper.icon-grayscale {
  background: linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%);
  box-shadow: none;
  filter: grayscale(100%);
  opacity: 0.75;
}

.seat-card-body {
  flex: 1;
  min-width: 0;
}

.app-name {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  cursor: pointer;
  border-radius: 3px;
  padding: 1px 4px;
  margin: -1px -4px;
}

.app-name:hover {
  background: #eef2ff;
}

.seat-name-editor {
  width: 100%;
}

.seat-name-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #6366f1;
  outline: none;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.2;
  height: 20px;
  background: #fff;
}

.seat-auditor {
  margin-top: 2px;
  font-size: 11px;
  line-height: 1.2;
}

.auditor-assigned {
  color: #059669;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.auditor-unassigned {
  color: #9ca3af;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.app-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
  align-items: flex-end;
}

.action-btn {
  border: 1px solid transparent;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  transition: all 0.15s ease;
}

.action-btn.assign,
.action-btn.unassign {
  padding: 3px 8px;
  font-size: 11px;
  height: 20px;
}

.action-btn:hover:not(:disabled) {
  background: #eef2ff;
  color: #4f46e5;
  border-color: #e0e7ff;
}

.action-btn.assign:hover:not(:disabled) {
  background: #ecfdf5;
  color: #059669;
  border-color: #d1fae5;
}

.action-btn.unassign:hover:not(:disabled) {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fee2e2;
}

.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.warning-box {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 14px 16px;
  margin-top: 8px;
}

.warning-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.warning-icon {
  color: #ea580c;
  font-size: 16px;
}

.warning-title {
  font-size: 14px;
  font-weight: 600;
  color: #c2410c;
}

.warning-message {
  font-size: 13px;
  color: #9a3412;
  line-height: 1.5;
  margin: 0 0 12px 0;
}

.acknowledge-checkbox {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #78350f;
}

.acknowledge-checkbox ::v-deep .el-checkbox__label {
  font-size: 13px;
  color: #78350f;
}
</style>
